import React, { useContext, useEffect, useState, useCallback, useRef } from "react";
import {
  Image,
  Spinner,
} from "@nextui-org/react";
import blogApi from "../../services/blogApi.js";
import Logo from "../../Assets/Images/logo.png";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useClerk } from "@clerk/clerk-react";
import { AuthContext } from "Context/AuthContext";

const NavbarComponent = () => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [isSearchOpen, setIsSearchOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [searchResults, setSearchResults] = React.useState([]);
  const [isSearching, setIsSearching] = React.useState(false);
  const searchTimeoutRef = useRef(null);
  const [openSubmenu, setOpenSubmenu] = useState(null);

  const menuItems = [
    { title: "Home", link: "/" },
    { title: "Tour", hasSubmenu: true },
    { title: "Download", link: "/download" },
    { title: "Contact Us", link: "/contact-us" },
  ];

  const submenuItems = [
    { title: "Home", link: "/home" },
    { title: "Ventures", link: "/ventures" },
    { title: "Events", link: "/events" },
    { title: "Escapes", link: "/escape" },
  ];

  const socialLinks = [
    { url: "https://www.facebook.com/thefrondapp", icon: "facebook", label: "Facebook" },
    { url: "https://x.com/TheFrondApp", icon: "x", label: "X" },
    { url: "https://www.instagram.com/thefrondapp", icon: "instagram", label: "Instagram" },
    { url: "https://www.linkedin.com/company/thefrondapp/?viewAsMember=true", icon: "linkedin", label: "LinkedIn" },
  ];

  const { pathname } = useLocation();
  const formattedPathname = pathname.replace(/\/+$/, "");

  const { signOut } = useClerk();
  const navigate = useNavigate();

  function logout() {
    localStorage.removeItem("token");
    setToken(null);
    navigate("/login");
  }

  const { token, setToken } = useContext(AuthContext);
  const isSignedIn = localStorage.getItem("token");

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "token") {
        if (!event.newValue) {
          setToken(null);
          navigate("/login");
          localStorage.removeItem("token");
          sessionStorage.clear();
        } else if (localStorage.getItem("token") !== token) {
          setToken(null);
          navigate("/login");
          localStorage.removeItem("token");
          sessionStorage.clear();
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  // Handle search with direct axios
  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    if (query.length > 2) {
      searchTimeoutRef.current = setTimeout(async () => {
        setIsSearching(true);
        try {
          const authToken = localStorage.getItem("token");
          const results = await blogApi.search(query, authToken);
          setSearchResults(results);
        } catch (error) {
          console.error('Search error:', error);
          setSearchResults([]);
        } finally {
          setIsSearching(false);
        }
      }, 500);
    } else {
      setSearchResults([]);
    }
  }, []);

  const handleCloseSearch = () => {
    setIsSearchOpen(false);
    setSearchQuery("");
    setSearchResults([]);
  };

  // Social Media Icon Component
  const SocialIcon = ({ type, className = "w-5 h-5" }) => {
    switch (type) {
      case "facebook":
        return (
          <svg className={className} fill="currentColor" viewBox="0 0 24 24">
            <path d="M18.77 7.46H14.5v-1.9c0-.9.6-1.1 1-1.1h3V.5h-4.33C10.24.5 9.5 3.44 9.5 5.32v2.15h-3v4h3v12h5v-12h3.85l.42-4z" />
          </svg>
        );
      case "x":
        return (
          <svg className={className} fill="currentColor" viewBox="0 0 24 24">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
          </svg>
        );
      case "instagram":
        return (
          <svg className={className} fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zM12 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
          </svg>
        );
      case "linkedin":
        return (
          <svg className={className} fill="currentColor" viewBox="0 0 24 24">
            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451c.979 0 1.771-.773 1.771-1.729V1.729C24 .774 23.204 0 22.225 0z" />
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <header className="page-header w-full bg-white border-b border-gray-100 sticky top-0 z-[99999]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16">
            {/* Logo - Left side */}
            <div className="flex-shrink-0">
              <Link to={"/"} className="block" aria-label="Homepage">
                <Image 
                  src={Logo} 
                  style={{ width: "100px", maxHeight: "32px" }} 
                  className="object-contain"
                />
              </Link>
            </div>

            {/* Right side - Icons */}
            <div className="flex items-center gap-3 sm:gap-4">
              {/* Search Icon */}
              <button
                onClick={() => setIsSearchOpen(!isSearchOpen)}
                className="p-1 text-gray-700 hover:text-gray-900 transition-colors"
                aria-label="Search"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>

              {/* Events Icon - E - Links to Events page */}
              <Link
                to={"/blog"}
                className="p-1 text-gray-700 hover:text-gray-900 transition-colors font-bold text-lg opacity-80"
                aria-label="Events"
              >
                <span className="w-5 h-5 flex items-center justify-center font-serif">E</span>
              </Link>

              {/* Profile Icon */}
              {token || isSignedIn ? (
                <button
                  onClick={() => {
                    signOut({ redirectUrl: "/" });
                    logout();
                  }}
                  className="text-xs font-semibold tracking-wide text-gray-700 hover:text-gray-900 transition-colors"
                >
                  LOG OUT
                </button>
              ) : (
                <button
                  onClick={() => navigate("/signup")}
                  className="p-1 text-gray-700 hover:text-gray-900 transition-colors"
                  aria-label="Sign up"
                >
                 <svg className="w-6 h-6 text-gray-500" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                  <circle cx="12" cy="9" r="3" stroke="currentColor" strokeWidth="2"/>
                  <path d="M6 18c0-3 3-5 6-5s6 2 6 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>

                </button>
              )}
              
              {/* Menu Button (Hamburger) */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="p-1 text-gray-700 hover:text-gray-900 transition-colors"
                aria-label="Toggle menu"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {isMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isSearchOpen ? 'max-h-96 opacity-100 border-t border-gray-100' : 'max-h-0 opacity-0'}`}>
          <div className="px-4 sm:px-6 lg:px-8 py-3 bg-white">
            <div className="relative max-w-2xl mx-auto">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Search blogs, articles, and more..."
                className="w-full px-4 py-2 pl-10 pr-4 text-gray-700 border border-gray-200 rounded-full focus:outline-none focus:border-gray-300 focus:ring-1 focus:ring-gray-300"
                autoFocus={isSearchOpen}
              />
              <svg className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              {searchQuery && (
                <button
                  onClick={() => {
                    setSearchQuery("");
                    setSearchResults([]);
                  }}
                  className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
            
            {/* Search Results */}
            {isSearching ? (
              <div className="max-w-2xl mx-auto mt-2 bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-center">
                <Spinner size="sm" />
                <p className="text-sm text-gray-500 mt-2">Searching Events...</p>
              </div>
            ) : searchResults.length > 0 ? (
              <div className="max-w-2xl mx-auto mt-2 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden max-h-80 overflow-y-auto">
                {searchResults.map((result) => (
                  <Link
                    key={result.id}
                    to={`/blog/${result.slug || result.id}`}
                    onClick={handleCloseSearch}
                    className="block px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
                  >
                    <p className="text-sm font-medium text-gray-900">{result.title}</p>
                    {result.excerpt && (
                      <p className="text-xs text-gray-500 mt-1 line-clamp-1">{result.excerpt}</p>
                    )}
                    <span className="inline-block px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full mt-1">
                      {/* {result.type || 'Event'}  result.type will write blog*/}
                      {'Event'}
                    </span>
                  </Link>
                ))}
              </div>
            ) : searchQuery.length > 2 ? (
              <div className="max-w-2xl mx-auto mt-2 bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-center">
                <p className="text-sm text-gray-500">No Events found for "{searchQuery}". Try different keywords.</p>
              </div>
            ) : null}
          </div>
        </div>
      </header>

      {/* Side Menu */}
      <div className={`fixed inset-y-0 right-0 w-full max-w-sm bg-white shadow-2xl transform transition-transform duration-300 ease-in-out z-[9999] ${isMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="flex flex-col h-full">
          {/* Side Menu Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-100">
            <Image 
              src={Logo} 
              style={{ width: "90px", maxHeight: "28px" }} 
              className="object-contain"
            />
            <button
              onClick={() => setIsMenuOpen(false)}
              className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Side Menu Items */}
          <div className="flex-1 overflow-y-auto py-1">
            {menuItems.map((item, index) => (
              <div key={index} className="border-b border-gray-50">
                {item.hasSubmenu ? (
                  <div>
                    <button
                      onClick={() => setOpenSubmenu(openSubmenu === index ? null : index)}
                      className="w-full flex items-center justify-between px-5 py-4 text-left text-gray-800 font-medium hover:bg-gray-50 transition-colors"
                    >
                      <span>{item.title}</span>
                      <svg className={`w-4 h-4 transition-transform duration-200 ${openSubmenu === index ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    <div className={`overflow-hidden transition-all duration-300 ${openSubmenu === index ? 'max-h-96' : 'max-h-0'}`}>
                      <div className="bg-gray-50/50 py-2">
                        {submenuItems.map((subItem, subIdx) => (
                          <Link
                            key={subIdx}
                            onClick={() => setIsMenuOpen(false)}
                            className="block px-5 py-3 pl-10 text-gray-600 text-sm hover:text-gray-900 hover:bg-gray-100 transition-colors"
                            to={subItem.link}
                          >
                            {subItem.title}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <Link
                    onClick={() => setIsMenuOpen(false)}
                    className="block px-5 py-4 text-gray-800 font-medium hover:bg-gray-50 transition-colors"
                    to={item.link}
                  >
                    {item.title}
                  </Link>
                )}
              </div>
            ))}
          </div>
          
          {/* Newsletter Section */}
          <div className="p-5 border-t border-gray-100 bg-gray-50/50">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Be the first to know</h3>
            <p className="text-sm text-gray-600 mb-4">
              Subscribe to our newsletter and never miss a new post.
            </p>
            <button
              onClick={() => {
                navigate("/signup");
                setIsMenuOpen(false);
              }}
              className="w-full py-3 px-6 text-black border border-pink-500 rounded-full text-sm font-semibold hover:opacity-90 transition-all"
            >
              Sign up for emails
            </button>
          </div>
          
          {/* Footer with Auth and Social */}
          <div className="p-5 border-t border-gray-100">
            {token || isSignedIn ? (
              <button
                onClick={() => {
                  signOut({ redirectUrl: "/" });
                  logout();
                  setIsMenuOpen(false);
                }}
                className="w-full py-3 text-center text-gray-700 border border-gray-300 rounded-full text-sm font-medium hover:bg-gray-50 transition-colors mb-6"
              >
                Log Out
              </button>
            ) : (
              <Link
                to={"/login"}
                onClick={() => setIsMenuOpen(false)}
                className="block w-full py-3 text-center text-gray-700 border border-gray-300 rounded-full text-sm font-medium mb-3 hover:bg-gray-50 transition-colors"
              >
                Log In
              </Link>
            )}

            {/* Social Links */}
            <div className="pt-4 border-t border-gray-100">
              <p className="text-xs text-gray-400 text-center mb-3">Follow us</p>
              <div className="flex justify-center gap-6">
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-500 hover:text-gray-800 transition-colors"
                    aria-label={social.label}
                  >
                    <SocialIcon type={social.icon} className="w-5 h-5" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Overlay */}
      {isMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsMenuOpen(false)}
        />
      )}
    </>
  );
};

export default NavbarComponent;